/* eslint-disable jsx-a11y/control-has-associated-label */
/* eslint-disable no-use-before-define */
import React, { useEffect, useMemo, useRef, useState } from "react";
// ANT DESIGN COMPONENTS
import { Dropdown } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faGear,
  faMinimize,
  faExpand,
  faTableCells,
  faSave,
  faElevator,
  faDesktop,
  faCircleCheck,
  faTerminal,
  faBuilding,
  faCircleInfo,
  faWifi,
  faServer,
  faCircleExclamation,
  faReplyAll,
  faDoorOpen,
  faTriangleExclamation,
  faEyeSlash,
  faEye,
  faBorderAll,
  faArrowDownAZ,
  faArrowDownWideShort,
  faSort,
  faLayerGroup,
  faCalendarCheck,
  faTableColumns,
  faWindowRestore,
} from "@fortawesome/free-solid-svg-icons";
// REDUX
import { useSelector, useDispatch } from "react-redux";
import {
  updateEnableDragAndDrop,
  updateShowFullView,
  updateMonitoringGridType,
  updateShowQueueInfo,
  updateSocketConnection,
  updateOpenGlobalTerminal,
  updateOpenModalSaveGrid,
  updateOpenModalManageGrids,
  updateOpenReplayPanel,
  updateDoorUnknownAsOpening,
  updateHideUnconfiguredDoors,
  updateAutoSortGrid,
  updatePreviewFaultServiceMode,
  updateShowCommandDetails,
  updateTerminalSplitView,
  updateTerminalSplitOrientation,
} from "../../../redux/monitoring/monitoringSlice";
import { usePatchOrganizationSettingsMutation } from "../../../redux/organizations/organizationAPI";
// AUTHORIZATION
import GETJwtToken from "../../../redux/authentication/authentication";
import { Permission, hasPermission } from "../../../utils/validatePermissions";
// COMPONENTS
import OrganizationCascaderSelectMultiple from "../../organizationManagement/organizations/OrganizationCascaderSelectMultiple";
import OrgScheduledActionsDrawer from "./OrgScheduledActionsDrawer";
import DecodedFrontEndMessagesModal from "./DecodedFrontEndMessagesModal";
// INTERFACES
import { RootState } from "../../../app/store";
import { MonitoringViewTypeEnum } from "../../../interfaces/Monitoring.interface";
// UTILS
import { emptyFunctionDeclaration } from "../../../utils/utils";
import notifyCommandBlocked from "../../../utils/commandFeedback";

/**
 * Name for the server entry in the connection menu.
 *
 * Always returns something. The label used to be two `&&` expressions, so an
 * unset REACT_APP_EDGE_SERVER_CONNECTION collapsed both to `false` and rendered
 * a bare icon with no text — which reads as a rendering glitch rather than the
 * configuration problem it is. That build is exactly the one where every action
 * button is silently inert, so the menu should say so.
 */
export function resolveEdgeConnectionLabel(configuredMode: string | undefined): string {
  if (configuredMode === "EDGE") return "Edge Server";
  if (configuredMode === "OFFLINE") return "Offline Server";
  return "Server (not configured)";
}

function MonitoringHeader() {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const dispatch = useDispatch();
  const [t] = useTranslation("global");

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [scheduledActionsDrawerOpen, setScheduledActionsDrawerOpen] = useState(false);
  const [openPreDispatchModal, setOpenPreDispatchModal] = useState(false);

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const {
    enableDragAndDrop,
    showFullView,
    cascaderSelectMultipleSelectionOptions,
    monitoringGridType,
    showQueueInfo,
    socketConnection,
    openGlobalTerminal,
    openReplayPanel,
    doorUnknownAsOpening,
    hideUnconfiguredDoors,
    showCommandDetails,
    terminalSplitView,
    terminalSplitOrientation,
  } = useSelector((state: RootState) => state.monitoring);
  const { userLogged, theme } = useSelector((state: RootState) => state.home);
  const { permissionSet } = useSelector((state: RootState) => state.user);

  // The cascader stores each selection as a path array
  // [orgId, serverId, channelId, bankId] (see OrganizationCascaderSelectMultiple
  // buildCascaderArray). The scheduled-actions drawer scopes its fetch by
  // organization, so collapse the selected paths down to their distinct root
  // org ids. (The previous `as string[]` cast treated each path as a scalar
  // string, so the typeof-string filter discarded every entry → always empty.)
  const selectedOrganizationIds = useMemo<string[]>(() => {
    if (!Array.isArray(cascaderSelectMultipleSelectionOptions)) return [];
    const orgIds = (cascaderSelectMultipleSelectionOptions as unknown as string[][])
      .map((path) => (Array.isArray(path) ? path[0] : undefined))
      .filter((id): id is string => typeof id === "string" && id !== "");
    return Array.from(new Set(orgIds));
  }, [cascaderSelectMultipleSelectionOptions]);

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */
  const [patchOrganizationSettings] = usePatchOrganizationSettingsMutation();
  const previewTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (previewTimeoutRef.current) {
        clearTimeout(previewTimeoutRef.current);
      }
    },
    [],
  );

  const triggerFaultServiceModePreview = () => {
    dispatch(updatePreviewFaultServiceMode(true));
    if (previewTimeoutRef.current) {
      clearTimeout(previewTimeoutRef.current);
    }
    previewTimeoutRef.current = setTimeout(() => {
      dispatch(updatePreviewFaultServiceMode(false));
    }, 5000);
  };

  // ************************************************ */
  // DROPDOWN MENU ITEMS **************************** */
  const isMasterAdmin = userLogged?.role?.name === process.env.REACT_APP_ROLE_MASTER_NAME;

  const allToolsMenuItems = [
    {
      key: "1",
      label: (
        <div
          className="monitoring__200pxBox"
          role="button"
          tabIndex={0}
          onKeyDown={emptyFunctionDeclaration}
          onClick={() => {
            if (
              cascaderSelectMultipleSelectionOptions &&
              cascaderSelectMultipleSelectionOptions.length > 0
            ) {
              dispatch(updateOpenModalSaveGrid(true));
            }
          }}
        >
          <span>
            <FontAwesomeIcon icon={faSave} className="generalStyles__mrFix generalStyles__info" />
            {t("monitoring.saveGrid")}
          </span>
        </div>
      ),
    },
    {
      key: "2",
      label: (
        <div
          className="monitoring__200pxBox"
          role="button"
          tabIndex={0}
          onKeyDown={emptyFunctionDeclaration}
          onClick={() => {
            if (
              cascaderSelectMultipleSelectionOptions &&
              cascaderSelectMultipleSelectionOptions.length > 0
            ) {
              dispatch(updateOpenModalManageGrids(true));
            }
          }}
        >
          <span>
            <FontAwesomeIcon icon={faGear} className="generalStyles__mrFix generalStyles__info" />
            {t("monitoring.manageGrids")}
          </span>
        </div>
      ),
    },
    {
      key: "3",
      label: (
        <div
          className="monitoring__200pxBox"
          role="button"
          tabIndex={0}
          onKeyDown={emptyFunctionDeclaration}
          onClick={() => {
            dispatch(updateEnableDragAndDrop(!enableDragAndDrop));
          }}
        >
          {enableDragAndDrop ? (
            <span>
              {" "}
              <FontAwesomeIcon
                icon={faTableCells}
                className="generalStyles__mrFix generalStyles__info"
              />
              {t("monitoring.disableDragAndDrop")}
            </span>
          ) : (
            <span>
              {" "}
              <FontAwesomeIcon
                icon={faTableCells}
                className="generalStyles__mrFix generalStyles__info"
              />
              {t("monitoring.enableDragAndDrop")}
            </span>
          )}
        </div>
      ),
    },
    {
      key: "5",
      label: (
        <div
          className="monitoring__150pxBox"
          role="button"
          tabIndex={0}
          onKeyDown={emptyFunctionDeclaration}
          onClick={() => {
            dispatch(updateOpenGlobalTerminal(!openGlobalTerminal));
            dispatch(updateShowQueueInfo(false));
            dispatch(updateOpenReplayPanel(false));
          }}
        >
          <span>
            {openGlobalTerminal ? (
              <FontAwesomeIcon
                icon={faCircleCheck}
                className="generalStyles__mrFix generalStyles__success"
              />
            ) : (
              <FontAwesomeIcon
                icon={faTerminal}
                className="generalStyles__mrFix generalStyles__info"
              />
            )}{" "}
            {openGlobalTerminal ? "Close Terminal" : "Open Terminal"}
          </span>
        </div>
      ),
    },
    {
      key: "5a",
      label: (
        <div
          className="monitoring__200pxBox"
          role="button"
          tabIndex={0}
          onKeyDown={emptyFunctionDeclaration}
          onClick={() => {
            dispatch(updateTerminalSplitView(!terminalSplitView));
          }}
        >
          <span>
            {terminalSplitView ? (
              <FontAwesomeIcon
                icon={faCircleCheck}
                className="generalStyles__mrFix generalStyles__success"
              />
            ) : (
              <FontAwesomeIcon
                icon={faTableColumns}
                className="generalStyles__mrFix generalStyles__info"
              />
            )}
            {terminalSplitView ? "Terminal Split View On" : "Terminal Split View"}
          </span>
        </div>
      ),
    },
    {
      key: "5b",
      label: (
        <div
          className="monitoring__200pxBox"
          role="button"
          tabIndex={0}
          onKeyDown={emptyFunctionDeclaration}
          onClick={() => {
            dispatch(
              updateTerminalSplitOrientation(
                terminalSplitOrientation === "horizontal" ? "vertical" : "horizontal",
              ),
            );
          }}
        >
          <span>
            <FontAwesomeIcon
              icon={faWindowRestore}
              className="generalStyles__mrFix generalStyles__info"
            />
            {terminalSplitOrientation === "horizontal" ? "Split: Stacked" : "Split: Side by Side"}
          </span>
        </div>
      ),
    },
    {
      key: "6",
      label: (
        <div
          className="monitoring__150pxBox"
          role="button"
          tabIndex={0}
          onKeyDown={emptyFunctionDeclaration}
          onClick={() => {
            dispatch(updateShowQueueInfo(!showQueueInfo));
            dispatch(updateOpenGlobalTerminal(false));
            dispatch(updateOpenReplayPanel(false));
          }}
        >
          <span>
            {showQueueInfo ? (
              <FontAwesomeIcon
                icon={faCircleCheck}
                className="generalStyles__mrFix generalStyles__success"
              />
            ) : (
              <FontAwesomeIcon
                icon={faTerminal}
                className="generalStyles__mrFix generalStyles__info"
              />
            )}
            {showQueueInfo ? (
              <>{t("monitoring.hideQueueInfo")}</>
            ) : (
              <>{t("monitoring.showQueueInfo")}</>
            )}
          </span>
        </div>
      ),
    },
    {
      key: "7",
      label: (
        <div
          className="monitoring__150pxBox"
          role="button"
          tabIndex={0}
          onKeyDown={emptyFunctionDeclaration}
          onClick={() => {
            dispatch(updateOpenReplayPanel(!openReplayPanel));
            dispatch(updateOpenGlobalTerminal(false));
            dispatch(updateShowQueueInfo(false));
          }}
        >
          <span>
            {openReplayPanel ? (
              <FontAwesomeIcon
                icon={faCircleCheck}
                className="generalStyles__mrFix generalStyles__success"
              />
            ) : (
              <FontAwesomeIcon
                icon={faReplyAll}
                className="generalStyles__mrFix generalStyles__info"
              />
            )}
            {openReplayPanel ? (
              <>{t("general.hideReplayPanel")}</>
            ) : (
              <>{t("general.showReplayPanel")}</>
            )}
          </span>
        </div>
      ),
    },
    {
      key: "8",
      label: (
        <div
          className="monitoring__200pxBox"
          role="button"
          tabIndex={0}
          onKeyDown={emptyFunctionDeclaration}
          onClick={async () => {
            const newValue = !doorUnknownAsOpening;
            dispatch(updateDoorUnknownAsOpening(newValue));
            const token = await GETJwtToken();
            patchOrganizationSettings({
              id: userLogged.organizationId,
              token,
              body: { door_unknown_as_opening: newValue },
            });
          }}
        >
          <span>
            {doorUnknownAsOpening ? (
              <FontAwesomeIcon
                icon={faDoorOpen}
                className="generalStyles__mrFix generalStyles__success"
              />
            ) : (
              <FontAwesomeIcon
                icon={faTriangleExclamation}
                className="generalStyles__mrFix generalStyles__info"
              />
            )}
            {doorUnknownAsOpening ? "Door Opening Mode" : "Door Warning Mode"}
          </span>
        </div>
      ),
    },
    {
      key: "9",
      label: (
        <div
          className="monitoring__200pxBox"
          role="button"
          tabIndex={0}
          onKeyDown={emptyFunctionDeclaration}
          onClick={async () => {
            const newValue = !hideUnconfiguredDoors;
            dispatch(updateHideUnconfiguredDoors(newValue));
            const token = await GETJwtToken();
            patchOrganizationSettings({
              id: userLogged.organizationId,
              token,
              body: { hide_unconfigured_doors: newValue },
            });
          }}
        >
          <span>
            {hideUnconfiguredDoors ? (
              <FontAwesomeIcon
                icon={faEyeSlash}
                className="generalStyles__mrFix generalStyles__success"
              />
            ) : (
              <FontAwesomeIcon icon={faEye} className="generalStyles__mrFix generalStyles__info" />
            )}
            {hideUnconfiguredDoors ? "Hide Unconfigured Doors" : "Show All Doors"}
          </span>
        </div>
      ),
    },
    {
      key: "10",
      label: (
        <div
          className="monitoring__200pxBox"
          role="button"
          tabIndex={0}
          onKeyDown={emptyFunctionDeclaration}
          onClick={triggerFaultServiceModePreview}
        >
          <span>
            <FontAwesomeIcon icon={faEye} className="generalStyles__mrFix generalStyles__info" />
            Preview Fault + Service Mode
          </span>
        </div>
      ),
    },
    {
      key: "11",
      label: (
        <div
          className="monitoring__200pxBox"
          role="button"
          tabIndex={0}
          onKeyDown={emptyFunctionDeclaration}
          onClick={() => {
            dispatch(updateShowCommandDetails(!showCommandDetails));
          }}
        >
          <span>
            {showCommandDetails ? (
              <FontAwesomeIcon
                icon={faCircleCheck}
                className="generalStyles__mrFix generalStyles__success"
              />
            ) : (
              <FontAwesomeIcon
                icon={faCircleInfo}
                className="generalStyles__mrFix generalStyles__info"
              />
            )}
            {showCommandDetails ? "Hide Command Details" : "Show Command Details"}
          </span>
        </div>
      ),
    },
    {
      key: "12",
      label: (
        <div
          className="monitoring__200pxBox"
          role="button"
          tabIndex={0}
          onKeyDown={emptyFunctionDeclaration}
          onClick={() => {
            setOpenPreDispatchModal(true);
          }}
        >
          <span>
            <FontAwesomeIcon
              icon={faTerminal}
              className="generalStyles__mrFix generalStyles__info"
            />
            Open Decoded WS Messages
          </span>
        </div>
      ),
    },
  ];

  // The Terminal itself is master-admin only, so its split-view controls are
  // too — offering a split for a panel the user cannot open is a dead menu row.
  const TERMINAL_ONLY_KEYS = ["5", "5a", "5b"];
  // Saving and managing grid layouts are each gated by their own permission;
  // without it the entry is hidden rather than shown inert. `canSaveGrid`
  // (monitor-grid-create) is also the Monitoring Dashboard Grids block's gate,
  // so it decides the cascader's Saved Grids picker too.
  const canSaveGrid = hasPermission(Permission.MonitorGridCreate, permissionSet ?? []);
  const canManageGrids = hasPermission(Permission.MonitorGridDelete, permissionSet ?? []);
  const SAVE_GRID_KEY = "1";
  const MANAGE_GRIDS_KEY = "2";
  const toolsMenuItems = allToolsMenuItems.filter((item) => {
    if (TERMINAL_ONLY_KEYS.includes(item.key)) return isMasterAdmin;
    if (item.key === SAVE_GRID_KEY) return canSaveGrid;
    if (item.key === MANAGE_GRIDS_KEY) return canManageGrids;
    return true;
  });

  const sortMenuItems = [
    {
      key: "sort-1",
      label: (
        <div
          className="monitoring__200pxBox"
          role="button"
          tabIndex={0}
          onKeyDown={emptyFunctionDeclaration}
          onClick={() => {
            dispatch(updateAutoSortGrid("alphabetical"));
          }}
        >
          <span>
            <FontAwesomeIcon
              icon={faArrowDownAZ}
              className="generalStyles__mrFix generalStyles__info"
            />
            {t("monitoring.sortAlphabetically")}
          </span>
        </div>
      ),
    },
    {
      key: "sort-2",
      label: (
        <div
          className="monitoring__200pxBox"
          role="button"
          tabIndex={0}
          onKeyDown={emptyFunctionDeclaration}
          onClick={() => {
            dispatch(updateAutoSortGrid("byType"));
          }}
        >
          <span>
            <FontAwesomeIcon
              icon={faArrowDownWideShort}
              className="generalStyles__mrFix generalStyles__info"
            />
            {t("monitoring.sortByType")}
          </span>
        </div>
      ),
    },
    {
      key: "sort-3",
      label: (
        <div
          className="monitoring__200pxBox"
          role="button"
          tabIndex={0}
          onKeyDown={emptyFunctionDeclaration}
          onClick={() => {
            dispatch(updateAutoSortGrid("bySize"));
          }}
        >
          <span>
            <FontAwesomeIcon
              icon={faBorderAll}
              className="generalStyles__mrFix generalStyles__info"
            />
            {t("monitoring.sortBySize")}
          </span>
        </div>
      ),
    },
    {
      key: "sort-4",
      label: (
        <div
          className="monitoring__200pxBox"
          role="button"
          tabIndex={0}
          onKeyDown={emptyFunctionDeclaration}
          onClick={() => {
            dispatch(updateAutoSortGrid("byTypeAligned"));
          }}
        >
          <span>
            <FontAwesomeIcon
              icon={faLayerGroup}
              className="generalStyles__mrFix generalStyles__info"
            />
            {t("monitoring.sortByTypeAligned")}
          </span>
        </div>
      ),
    },
  ];

  const monitorMenuItems = [
    {
      key: "1",
      label: (
        <div
          className="monitoring__150pxBox"
          role="button"
          tabIndex={0}
          onKeyDown={emptyFunctionDeclaration}
          onClick={() => {
            dispatch(updateMonitoringGridType(MonitoringViewTypeEnum.EXPANDED_VIEW));
          }}
        >
          <span>
            {monitoringGridType === MonitoringViewTypeEnum.EXPANDED_VIEW ? (
              <FontAwesomeIcon
                icon={faCircleCheck}
                className="generalStyles__mrFix generalStyles__success"
              />
            ) : (
              <FontAwesomeIcon
                icon={faBuilding}
                className="generalStyles__mrFix generalStyles__info"
              />
            )}
            {t("monitoring.expandedView")}
          </span>
        </div>
      ),
    },
    {
      key: "2",
      label: (
        <div
          className="monitoring__150pxBox"
          role="button"
          tabIndex={0}
          onKeyDown={emptyFunctionDeclaration}
          onClick={() => {
            dispatch(updateMonitoringGridType(MonitoringViewTypeEnum.OVER_HEAD_VIEW));
          }}
        >
          <span>
            {monitoringGridType === MonitoringViewTypeEnum.OVER_HEAD_VIEW ? (
              <FontAwesomeIcon
                icon={faCircleCheck}
                className="generalStyles__mrFix generalStyles__success"
              />
            ) : (
              <FontAwesomeIcon
                icon={faElevator}
                className="generalStyles__mrFix generalStyles__info"
              />
            )}
            {t("monitoring.overHeadView")}
          </span>
        </div>
      ),
    },
    {
      key: "3",
      label: (
        <div
          className="monitoring__150pxBox"
          role="button"
          tabIndex={0}
          onKeyDown={emptyFunctionDeclaration}
          onClick={() => {
            dispatch(updateMonitoringGridType(MonitoringViewTypeEnum.STATUS_VIEW));
          }}
        >
          <span>
            {monitoringGridType === MonitoringViewTypeEnum.STATUS_VIEW ? (
              <FontAwesomeIcon
                icon={faCircleCheck}
                className="generalStyles__mrFix generalStyles__success"
              />
            ) : (
              <FontAwesomeIcon
                icon={faCircleInfo}
                className="generalStyles__mrFix generalStyles__info"
              />
            )}
            {t("monitoring.statusView")}
          </span>
        </div>
      ),
    },
    /* {
      key: "4",
      label: hasPermission(Permission.MonitorGridTypeMapView, permissionSet ?? []) ? (
        <div
          className="monitoring__150pxBox"
          role="button"
          tabIndex={0}
          onKeyDown={emptyFunctionDeclaration}
          onClick={() => {
            dispatch(updateMonitoringGridType(MonitoringViewTypeEnum.MAP_VIEW));
          }}
        >
          <span>
            {monitoringGridType === MonitoringViewTypeEnum.MAP_VIEW ? (
              <FontAwesomeIcon
                icon={faCircleCheck}
                className="generalStyles__mrFix generalStyles__success"
              />
            ) : (
              <FontAwesomeIcon icon={faMap} className="generalStyles__mrFix generalStyles__info" />
            )}
            {t("monitoring.mapView")}
          </span>
        </div>
      ) : null,
    }, */
  ];

  const edgeConnectionLabel = resolveEdgeConnectionLabel(
    process.env.REACT_APP_EDGE_SERVER_CONNECTION,
  );
  const connectionMenuItems = [
    {
      key: "1",
      label: (
        <div
          className="monitoring__150pxBox"
          role="button"
          tabIndex={0}
          onKeyDown={emptyFunctionDeclaration}
          onClick={() => {
            if (process.env.REACT_APP_EDGE_SERVER_CONNECTION === "EDGE") {
              dispatch(updateSocketConnection("EDGE"));
            } else if (process.env.REACT_APP_EDGE_SERVER_CONNECTION === "OFFLINE") {
              dispatch(updateSocketConnection("OFFLINE"));
            } else {
              // Neither branch matched, so this entry cannot select anything.
              // It used to render a bare icon with NO text and swallow the
              // click, which reads as "I chose Edge Server" while leaving
              // socketConnection empty — the state in which every action button
              // is silently inert.
              notifyCommandBlocked(
                "Server connection unavailable",
                "This build has no REACT_APP_EDGE_SERVER_CONNECTION value, so edge and offline monitoring cannot be selected. Commands will not reach a controller until it is set to EDGE or OFFLINE.",
                theme,
              );
            }
          }}
        >
          <span>
            {socketConnection === "EDGE" || socketConnection === "OFFLINE" ? (
              <FontAwesomeIcon
                icon={faCircleCheck}
                className="generalStyles__mrFix generalStyles__success"
              />
            ) : (
              <FontAwesomeIcon
                icon={faServer}
                className="generalStyles__mrFix generalStyles__info"
              />
            )}
            {edgeConnectionLabel}
          </span>
        </div>
      ),
    },
    {
      key: "2",
      label: (
        <div
          className="monitoring__150pxBox"
          role="button"
          tabIndex={0}
          onKeyDown={emptyFunctionDeclaration}
          onClick={() => {
            dispatch(updateSocketConnection("EMULATOR"));
          }}
        >
          <span>
            {socketConnection === "EMULATOR" ? (
              <FontAwesomeIcon
                icon={faCircleCheck}
                className="generalStyles__mrFix generalStyles__success"
              />
            ) : (
              <FontAwesomeIcon
                icon={faCircleExclamation}
                className="generalStyles__mrFix generalStyles__info"
              />
            )}
            Emulator
          </span>
        </div>
      ),
    },
  ];
  // ************************************************ */
  // FUNCTIONS ************************************** */
  const onClickFullView = () => {
    dispatch(updateShowFullView(!showFullView));
  };

  // ************************************************* */
  // USE EFFECT ************************************** */

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <>
      <div className={`generalStyles__spaceBetween mb-3 monitoring__headerContainer__${theme}`}>
        <div className="generalStyles__flex">
          {/** ******************************************* */}
          {/** CASCADER SELECT */}
          <div>
            <OrganizationCascaderSelectMultiple showSavedGrids={canSaveGrid} />
          </div>
          {hasPermission(Permission.ScheduledActionsView, permissionSet ?? []) && (
            <div>
              <div
                className="monitoring__barIcon"
                role="button"
                tabIndex={0}
                onKeyDown={emptyFunctionDeclaration}
                onClick={() => setScheduledActionsDrawerOpen(true)}
              >
                <FontAwesomeIcon
                  className={`generalStyles__themeIcon1__${theme}`}
                  icon={faCalendarCheck}
                />
              </div>
            </div>
          )}
        </div>
        <OrgScheduledActionsDrawer
          open={scheduledActionsDrawerOpen}
          onClose={() => setScheduledActionsDrawerOpen(false)}
          organizationId={userLogged?.organizationId ?? ""}
          organizationIds={selectedOrganizationIds.length > 0 ? selectedOrganizationIds : undefined}
        />
        <div className="generalStyles__flex">
          {/** ************************************************** */}
          {/** MONITOR ICON */}
          <div>
            <div
              className="monitoring__barIcon"
              role="button"
              tabIndex={0}
              onKeyDown={emptyFunctionDeclaration}
            >
              <Dropdown menu={{ items: monitorMenuItems }} placement="bottomLeft">
                <FontAwesomeIcon
                  className={`generalStyles__themeIcon1__${theme}`}
                  icon={faDesktop}
                />
              </Dropdown>
            </div>
          </div>
          {/** ************************************************** */}
          {/** GEAR ICON */}
          <div>
            <div
              className="monitoring__barIcon"
              role="button"
              tabIndex={0}
              onKeyDown={emptyFunctionDeclaration}
            >
              <Dropdown
                menu={{
                  items: toolsMenuItems,
                  style: { minWidth: 280 },
                }}
                placement="bottomLeft"
              >
                <FontAwesomeIcon className={`generalStyles__themeIcon1__${theme}`} icon={faGear} />
              </Dropdown>
            </div>
          </div>
          {/** ************************************************** */}
          {/** SORT ICON */}
          <div>
            <div
              className="monitoring__barIcon"
              role="button"
              tabIndex={0}
              onKeyDown={emptyFunctionDeclaration}
            >
              <Dropdown menu={{ items: sortMenuItems }} placement="bottomLeft">
                <FontAwesomeIcon className={`generalStyles__themeIcon1__${theme}`} icon={faSort} />
              </Dropdown>
            </div>
          </div>
          {/** ************************************************** */}
          {/** CONNECTION ICON */}
          {hasPermission(Permission.MonitorEmulator, permissionSet ?? []) && (
            <div>
              <div
                className="monitoring__barIcon"
                role="button"
                tabIndex={0}
                onKeyDown={emptyFunctionDeclaration}
              >
                <Dropdown menu={{ items: connectionMenuItems }} placement="bottomLeft">
                  <FontAwesomeIcon
                    className={`generalStyles__themeIcon1__${theme}`}
                    icon={faWifi}
                  />
                </Dropdown>
              </div>
            </div>
          )}
          {/** ************************************************** */}
          {/** EXPAND ICON */}
          <div>
            <div
              role="button"
              tabIndex={0}
              onClick={onClickFullView}
              onKeyDown={emptyFunctionDeclaration}
              className="monitoring__barIcon"
            >
              <FontAwesomeIcon
                className={`generalStyles__themeIcon1__${theme}`}
                icon={showFullView ? faMinimize : faExpand}
              />
            </div>
          </div>
        </div>
      </div>
      <DecodedFrontEndMessagesModal
        open={openPreDispatchModal}
        onClose={() => setOpenPreDispatchModal(false)}
      />
    </>
  );
}

export default MonitoringHeader;
